import { ANALYTICS_PAGE_BASE, TAB_ANALYTICS_NAME } from './my-ads.constants';
import type { TTabId } from './my-ads.types';
import type {
	TAnalyticsEvent,
	TCheckoutCartInput,
	TCheckoutConversionInput,
	TDataLayerAd,
	TUpgradePlanInput
} from '@/hooks/analytics';

/**
 * Builders dos eventos de analytics da página Meus Anúncios, em paridade com a
 * garagem (HOC `withAdobeLaunch` + componentes do HomeRefactor). Centralizados
 * aqui porque são disparados em mais de um lugar (página + modal de upgrade).
 */

/**
 * Pageview de uma aba em formato barra (igual garagem):
 * `/webmotors/garagem/meus-anuncios/<aba>` + pageType `meus-anuncios-<aba>`,
 * `tier: 3` (→ pageNameTier1/2/3) e os blocos site/user/page.
 */
const buildTabPageView = (tab: TTabId, recurringEvent: boolean): TAnalyticsEvent => {
	const name = TAB_ANALYTICS_NAME[tab];
	return {
		pageName: `${ANALYTICS_PAGE_BASE}/${name}`,
		pageType: `meus-anuncios-${name}`,
		flowType: 'garagem',
		use: ['site', 'user', 'page'],
		tier: 3,
		recurringEvent
	};
};

/**
 * Evento nomeado de ação no anúncio (`ativarAnuncio`, `desativarAnuncio`,
 * `excluirAnuncio`, `editarAnuncioSucesso`) — paridade com a garagem
 * (`setAdobeLaunchEvent({ ...objDataLayer, ad }, '<evento>')` / `onLaunch`).
 * Inclui os blocos site/user/page (aba atual) + ad.
 */
const buildAdEvent = (tab: TTabId, eventName: string, ad: TDataLayerAd): TAnalyticsEvent => ({
	...buildTabPageView(tab, true),
	eventName,
	use: ['site', 'user', 'page', 'ad'],
	ad
});

/**
 * Evento `turbinarAnuncio` — disparado quando o modal de upgrade carrega os
 * planos. pageName/pageType/tier fixos (upgrade-plano), com bloco `products`.
 */
const buildUpgradeEvent = (upgradePlans: ReadonlyArray<TUpgradePlanInput>): TAnalyticsEvent => ({
	eventName: 'turbinarAnuncio',
	pageName: `${ANALYTICS_PAGE_BASE}/ativos/upgrade-plano`,
	pageType: 'meus-anuncios-upgrade',
	flowType: 'garagem',
	use: ['site', 'user', 'page', 'products'],
	tier: 4,
	recurringEvent: true,
	upgradePlans
});

/**
 * Pageview do modal de upgrade (`/ativos/upgrade-plano`) — disparado quando o
 * modal é aberto na garagem. Mesmo pageName/pageType/tier do evento
 * `turbinarAnuncio` (que segue sendo o evento de clique), mas como pageview
 * (blocos site/user/page).
 */
const buildUpgradePageView = (): TAnalyticsEvent => ({
	pageName: `${ANALYTICS_PAGE_BASE}/ativos/upgrade-plano`,
	pageType: 'meus-anuncios-upgrade',
	flowType: 'garagem',
	use: ['site', 'user', 'page'],
	tier: 4,
	recurringEvent: true
});

const CHECKOUT_PAGE_BASE = `${ANALYTICS_PAGE_BASE}/ativos`;

type TCheckoutAnalyticsParams = {
	cart: TCheckoutCartInput;
	advertId: number;
};

type TCheckoutSuccessParams = TCheckoutAnalyticsParams & {
	transactionID: string;
	paymentType: string;
	creditCardFlag?: string;
};

/**
 * Pageview do checkout upgrade (seleção de pagamento) — paridade garagem
 * `/ativos/pagamento` + blocos cart/ad.
 */
const buildCheckoutPaymentPageView = ({
	cart,
	advertId
}: TCheckoutAnalyticsParams): TAnalyticsEvent => ({
	pageName: `${CHECKOUT_PAGE_BASE}/pagamento`,
	pageType: 'meus-anuncios-upgrade-pagamento',
	flowType: 'garagem',
	use: ['site', 'user', 'page', 'cart', 'ad'],
	tier: 4,
	checkoutCart: cart,
	ad: { id: String(advertId) }
});

/**
 * Pageview da tela final de sucesso do PIX ("Falta pouco… análise de confirmação
 * de dados", `/ativos/analise-confirmacao-dados`, disparado no /pix/sucesso).
 * Pageview simples (cart + ad), SEM conversion: a conversão/purchase do PIX sai
 * antes, na tela do QR (`/ativos/pagamento-sucesso`), em paridade com o
 * vender.next (a view `pix-awaiting` é quem dispara o purchase).
 */
const buildCheckoutAnalysisPageView = ({
	cart,
	advertId
}: TCheckoutAnalyticsParams): TAnalyticsEvent => ({
	pageName: `${CHECKOUT_PAGE_BASE}/analise-confirmacao-dados`,
	pageType: 'meus-anuncios-upgrade-analise-confirmacao-dados',
	flowType: 'garagem',
	use: ['site', 'user', 'page', 'cart', 'ad'],
	tier: 4,
	checkoutCart: cart,
	ad: { id: String(advertId) }
});

/**
 * Pageview de sucesso (`/ativos/pagamento-sucesso`) — cart + conversion + ad.
 * No cartão sai no sucesso do pagamento; no PIX sai na exibição do QR
 * (view `pix-awaiting` do vender.next), que é onde o purchase é contabilizado.
 */
const buildCheckoutSuccessPageView = ({
	cart,
	advertId,
	transactionID,
	paymentType,
	creditCardFlag
}: TCheckoutSuccessParams): TAnalyticsEvent => {
	const checkoutConversion: TCheckoutConversionInput = {
		transactionID,
		paymentType
	};

	if (creditCardFlag) {
		checkoutConversion.creditCardFlag = creditCardFlag;
	}

	return {
		pageName: `${CHECKOUT_PAGE_BASE}/pagamento-sucesso`,
		pageType: 'meus-anuncios-upgrade-pagamento-sucesso',
		flowType: 'garagem',
		use: ['site', 'user', 'page', 'ad', 'cart', 'conversion'],
		tier: 4,
		checkoutCart: cart,
		ad: { id: String(advertId) },
		checkoutConversion
	};
};

export {
	buildTabPageView,
	buildAdEvent,
	buildUpgradeEvent,
	buildUpgradePageView,
	buildCheckoutPaymentPageView,
	buildCheckoutAnalysisPageView,
	buildCheckoutSuccessPageView
};
export type { TCheckoutAnalyticsParams, TCheckoutSuccessParams };
